import { css, type Handle } from 'remix/ui'
import { routes } from '#universal/routes.ts'
import { docHref } from '#universal/docs-nav.ts'
import { nativeDisclosureCss } from '#universal/styles/style-primitives.ts'

type EntityExplainerLink = {
	href: string
	label: string
}

export type EntityExplainerCopy = {
	id: string
	question: string
	paragraphs: Array<string>
	learnMore?: Array<EntityExplainerLink>
}

const packagesIntegrationsMcpGuide = {
	href: docHref('packages-integrations-mcp'),
	label: 'Packages, integrations, and MCP servers',
} as const satisfies EntityExplainerLink

type EntityExplainerDefinition = EntityExplainerCopy & {
	match: (pathname: string) => boolean
}

function accountSection(href: string) {
	return (pathname: string) =>
		pathname === href || pathname.startsWith(`${href}/`)
}

const entityExplainerDefinitions: Array<EntityExplainerDefinition> = [
	{
		id: 'email',
		question: 'What is email?',
		match: accountSection(routes.accountEmail.href()),
		paragraphs: [
			"Every Kody account gets a personal inbox at your username on this deployment's email domain. Inbound mail is stored so automations can react to it, and your agent can send you notify-self messages or reply to stored threads.",
			'Use the inbox as an automation trigger — invoices to a +tag address, alerts from a job, or a daily digest that stays quiet until something actually happened. Compose and reply through your agent; this page is for browsing, classifying, and inspecting messages.',
		],
		learnMore: [
			{
				href: docHref('how-kody-works'),
				label: 'How Kody works',
			},
		],
	},
	{
		id: 'jobs',
		question: 'What is a job?',
		match: accountSection(routes.accountJobs.href()),
		paragraphs: [
			'A job is scheduled work that runs in the cloud on Cloudflare Workers, whether or not your computer is on. Package-owned jobs live with the saved package that declares them.',
			'Use a job for recurring or interval work that belongs to a package. From here you can inspect schedules, run a job now, and toggle kill switch or Preserve. Deferred one-shots belong on Workflows.',
		],
		learnMore: [
			{
				href: docHref('how-kody-works'),
				label: 'How Kody works',
			},
		],
	},
	{
		id: 'workflows',
		question: 'What is a workflow?',
		match: accountSection(routes.accountWorkflows.href()),
		paragraphs: [
			'A workflow is a deferred or long-running run created through kody:runtime workflows.create. Inline workflows carry their code; package workflows call a published export. Unlike jobs, workflows are one-shot durable work rather than recurring schedules.',
			'Use this page to inspect status, run time, and errors, and to cancel a run that has not finished yet.',
		],
		learnMore: [
			{
				href: docHref('how-kody-works'),
				label: 'How Kody works',
			},
		],
	},
	{
		id: 'webhooks',
		question: 'What is a webhook?',
		match: accountSection(routes.accountWebhooks.href()),
		paragraphs: [
			'A webhook is an inbound HTTP endpoint a package declares in package.json#kody.webhooks. Each name binds one export; when a provider such as GitHub, Stripe, or Sentry (or your own trusted client) POSTs to the minted URL, Kody runs that export.',
			"The URL is a credential and belongs to the package that declares it. This list shows every webhook across your packages; open one to mint, reveal, copy, rotate, or disable its URL in that package's settings. Agents connected over MCP can mint an opaque handle and apply it to GitHub on your behalf, but the URL itself only ever shows in package settings.",
		],
		learnMore: [
			{
				href: docHref('triggers'),
				label: 'Jobs, workflows, and webhooks',
			},
		],
	},
	{
		id: 'secrets',
		question: 'What is a secret?',
		match: accountSection(routes.accountSecrets.href()),
		paragraphs: [
			'A secret is a credential Kody stores for you — an API key, personal access token, or OAuth token. Your agent references secrets by name; Kody substitutes them at the network boundary and never returns the raw value to chat.',
			'Use secrets so your agent can call the services you already use without pasting keys into the conversation. Add API keys here; connect OAuth apps from Integrations.',
		],
		learnMore: [
			{
				href: docHref('account-secret-setup'),
				label: 'Secret setup guide',
			},
		],
	},
	{
		id: 'integrations',
		question: 'What is an integration?',
		match: accountSection(routes.accountIntegrations.href()),
		paragraphs: [
			'An integration is a connected service — usually OAuth — so Kody can act as you on that provider. You register your own OAuth client, and tokens land as your secrets.',
			'Use an integration when a provider needs a signed-in connection rather than a static API key. Tokens land as your secrets. Scope connections deliberately and revoke unused ones.',
			'The connection is yours. Packages use it; they do not own it. An integration is not a package and not an MCP server.',
		],
		learnMore: [
			packagesIntegrationsMcpGuide,
			{
				href: docHref('integration-bootstrap'),
				label: 'Integration bootstrap guide',
			},
		],
	},
	{
		id: 'connections',
		question: 'What is a connection?',
		match: accountSection(routes.accountConnections.href()),
		paragraphs: [
			'A connection is an AI host — Cursor, Claude, ChatGPT, Codex, a CLI — that has authorized against this Kody account over MCP. Every connected host reaches the same memories, secrets, packages, jobs, and email; Kody is the home they share, not a gateway.',
			'Add connection lists every agent Kody knows how to connect and shows that host’s install steps; the MCP URL works for any other host that speaks MCP. Revoke a host you no longer use from the connected list. Sign-in providers such as GitHub and Google stay on Overview. Remote MCP servers Kody calls on your behalf live on MCP servers.',
		],
		learnMore: [
			{
				href: docHref('connect-your-agent'),
				label: 'Connect your agent',
			},
			packagesIntegrationsMcpGuide,
		],
	},
	{
		id: 'mcp-servers',
		question: 'What is an MCP server?',
		match: accountSection(routes.accountMcpServers.href()),
		paragraphs: [
			'Kody can act as an MCP client: you add a remote MCP server, and its tools become callable as kody.mcp["server-name"].tool_name(...). This is the inverse of connecting your agent to Kody.',
			'Use this when another product already exposes MCP tools you want your Kody-connected agent to reach. Add a URL plus a bearer token, or complete OAuth when the server requires it.',
			'This is how agents connect, not package runtime. Prefer packages as the surface agents reach through Kody MCP.',
		],
		learnMore: [packagesIntegrationsMcpGuide],
	},
	{
		id: 'memories',
		question: 'What is a memory?',
		match: accountSection(routes.accountMemories.href()),
		paragraphs: [
			'A memory is a durable fact or preference Kody keeps about you across conversations — things like a preferred language or how you like to be notified. Agents retrieve a few relevant memories per task and must verify before writing.',
			'Browse, filter, and delete memories here. Ask your agent to remember something important; do not store secrets or credentials as memories.',
		],
		learnMore: [
			{
				href: docHref('what-is-kody'),
				label: 'What is Kody?',
			},
		],
	},
	{
		id: 'shared',
		question: 'What is a shared package?',
		match: accountSection(routes.accountShared.href()),
		paragraphs: [
			'Shared packages are invitations from one paid Kody account to another to use a package. Guests can read source and invoke. They cannot publish, write, or create jobs, apps, webhooks, or subscriptions on the shared package.',
			'Accept is required. Pin stays on the accepted publish until you approve a later one. Follow auto-accepts future publishes. Raw secret values stay hidden.',
		],
		learnMore: [
			{
				href: docHref('package-sharing'),
				label: 'Package sharing',
			},
		],
	},
	{
		id: 'waiting',
		question: 'What is waiting?',
		match: accountSection(routes.accountWaiting.href()),
		paragraphs: [
			'Waiting is the current-state queue of things only you can clear: verify email, reconnect an MCP server, promote a locked-package publish, confirm a pending email change, or finish setup.',
			'Items disappear when the gate clears. Activity is run history. Email is your mailbox. Vendor outages and operator work do not show up here.',
		],
	},
	{
		id: 'activity',
		question: 'What is activity?',
		match: accountSection(routes.accountActivity.href()),
		paragraphs: [
			'Activity is a short execution history for jobs, package apps, webhooks, and other runtimes. Open errors shows failures with logs and triage (open, ignored, or resolved). Recent runs lists the last week of successes and errors from the same records.',
			'Use Open errors to decide whether to ignore, resolve, or fix a failure. Switch to Recent runs to see what ran. Your agent can review the same data through the runs capabilities. A later successful run for the same job automatically resolves earlier open errors.',
		],
	},
	{
		id: 'usage',
		question: 'What is usage?',
		match: accountSection(routes.accountUsage.href()),
		paragraphs: [
			'Usage is how much of your plan you have consumed — stored email, job slots, workflow concurrency, and other finite entitlements.',
			'Check this page when something is quota-gated or you are deciding whether to upgrade. Limits are per signed-in user.',
		],
		learnMore: [
			{
				href: routes.pricing.href(),
				label: 'Plans and pricing',
			},
		],
	},
	{
		id: 'community',
		question: 'What is community?',
		match: (pathname) => pathname === routes.community.href(),
		paragraphs: [
			"Community is the public catalog of published packages on this deployment. A listing is a pinned snapshot of someone else's package, not a live link to their private copy.",
			'Browse and search without an account. Installing creates a fork you own — you can change it, schedule it, and publish your own version. Prefer a close public package before creating one from scratch.',
		],
		learnMore: [
			{
				href: docHref('what-is-kody'),
				label: 'What is Kody?',
			},
		],
	},
]

export function resolveEntityExplainer(
	pathname: string,
): EntityExplainerCopy | null {
	const entry = entityExplainerDefinitions.find((item) => item.match(pathname))
	if (!entry) return null
	return {
		id: entry.id,
		question: entry.question,
		paragraphs: entry.paragraphs,
		...(entry.learnMore ? { learnMore: entry.learnMore } : {}),
	}
}

const entityExplainerCss = {
	...nativeDisclosureCss,
	maxWidth: '60ch',
}

type EntityExplainerProps = {
	copy: EntityExplainerCopy
	marginTop?: string
}

export function EntityExplainer(handle: Handle<EntityExplainerProps>) {
	return () => (
		<details
			data-entity-explainer={handle.props.copy.id}
			mix={css({
				...entityExplainerCss,
				...(handle.props.marginTop
					? { marginTop: handle.props.marginTop }
					: {}),
			})}
		>
			<summary>{handle.props.copy.question}</summary>
			<div>
				{handle.props.copy.paragraphs.map((paragraph) => (
					<p key={paragraph}>{paragraph}</p>
				))}
				{handle.props.copy.learnMore?.map((link) => (
					<p key={link.href}>
						<a href={link.href}>{link.label}</a>
					</p>
				))}
			</div>
		</details>
	)
}
